import { useState } from 'react'

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import Icon from '@/components/ui/icon'
import { useToast } from '@/lib/hooks/use-toast'
import { User } from '@/types/user'

import PhotoCapture, { CapturedData } from './photo-capture'
import SignaturePad from './SignaturePad'
import { useAddSignatureUser, useCreateSignatureUser, useFetchSignatureByIdUser } from '../services'
import { useQueryClient } from '@tanstack/react-query'

interface SignatureDialogProps {
  user: User
  open: boolean
  onOpenChange?: (open: boolean) => void
}

export default function SignatureDialog({ user, open, onOpenChange }: SignatureDialogProps) {
  const { toast } = useToast()
  const [isLoading, setIsLoading] = useState(false)
  const [isPhotoCaptureComplete, setIsPhotoCaptureComplete] = useState(false)
  const { mutate: createSignature } = useCreateSignatureUser()
  const { mutate: addSignature } = useAddSignatureUser()
  const { data: signatureStatus } = user?.id ? useFetchSignatureByIdUser(user?.id) : { data: null }
  const queryClient = useQueryClient()

  const handlePhotoCaptureComplete = async (data: CapturedData) => {
    setIsLoading(true)

    try {
      await new Promise<void>((resolve, reject) => {
        createSignature(
          {
            fotoPessoa: data.fotoPessoa,
            fotoDoc: data.fotoDoc,
            local: data.local,
            maquina: data.maquina
          },
          {
            onSuccess: () => {
              toast({
                description: 'Foto capturada e informações salvas!'
              })
              setIsPhotoCaptureComplete(true)
              resolve()
            },
        onError: error => {
              toast({
                variant: 'destructive',
                description: 'Falha ao salvar informações.'
              })
              reject(error)
            }
          }
        )
      })
    } catch {
    } finally {
      setIsLoading(false)
    }
  }

  const handleSignatureComplete = (signatureData: File) => {
    if (!user?.id || !signatureStatus?.data.cpf) {
      toast({
        variant: 'destructive',
        description: 'Não foi possível identificar o usuário ou o CPF para assinatura.'
      })
      return
    }

    setIsLoading(true)

    addSignature(
      {
        cpf: signatureStatus.data.cpf,
        assinatura: signatureData
      },
      {
        onSuccess: () => {
          queryClient.refetchQueries({ queryKey: ['signature', user.id] })
          onOpenChange && onOpenChange(false)
          toast({
            description: 'Assinatura adicionada com sucesso!'
          })
        },
        onError: () => {
          toast({
            variant: 'destructive',
            description: 'Falha ao adicionar assinatura. Verifique os dados e tente novamente.'
          })
        },
        onSettled: () => {
          setIsLoading(false)
        }
      }
    )
  }

  return (
    <Dialog
      open={open}
      onOpenChange={o => {
        if (signatureStatus?.data.isModifica && open) {
          return
        }
        if (onOpenChange) {
          onOpenChange(o)
        }
      }}
    >
      <DialogContent
        className="max-h-screen overflow-y-auto "
        aria-describedby="signature-dialog-description"
        aria-description="Area de assinatura"
        onInteractOutside={e => {
          if (signatureStatus?.data.isModifica) {
            e.preventDefault()
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Assinatura</DialogTitle>
        </DialogHeader>

        <p id="signature-dialog-description" className="sr-only">
          Tela de captura e assinatura do usuário.
        </p>
        <div className="flex flex-col items-center">
          {isLoading ? (
            <Icon name="Loader" className="h-12 w-12 animate-spin text-blue-500" />
          ) : isPhotoCaptureComplete ? (
            <SignaturePad id_user={user?.id || 'default_id'} onComplete={handleSignatureComplete} />
          ) : (
            <PhotoCapture onComplete={handlePhotoCaptureComplete} user={true} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
